/**
 * @file Header du site public d'un établissement (logo, navigation, langue).
 * @module packages.common.layouts.public.Header
 *
 * Orchestrateur : calcule l'état partagé (langues disponibles, disponibilité
 * de la vente de bons cadeaux, page courante) et compose les sous-composants
 * du dossier `components/` (barre d'annonces, navigation desktop, sélecteur
 * de langue, menu mobile). La navigation n'est proposée que sur la page
 * d'accueil et quand la vente en ligne de bons cadeaux est disponible.
 */
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useVoucherTranslation} from 'packages/common/utils/multiLanguageHelper';
import {useLanguageReset} from 'packages/common/hooks/useLanguageReset';
import {
  AppBar,
  Toolbar,
  Box,
  CardMedia,
  Typography,
  useTheme,
  Stack,
  Container,
  alpha,
  IconButton,
  Drawer,
  useScrollTrigger,
} from '@mui/material';
import {useLocation, useParams, Link} from 'react-router-dom';
import {useResponsive} from 'packages/common/hooks/useResponsive';
import {paths} from 'packages/common/routes/paths';
import useLocales from 'packages/common/locales/use-locales';
import Iconify from 'packages/common/components/iconify';
import {isGiftVoucherOnlineSaleAvailable} from 'packages/common/utils/giftVoucherActivationHelper';
import {createGradientFromColor} from 'packages/common/utils/colorUtils';
import {FlowLanguageSelector} from 'packages/common/components/flow-language-selector';
import HeaderAnnouncementBar from './components/HeaderAnnouncementBar';
import HeaderDesktopNav from './components/HeaderDesktopNav';
import HeaderMobileMenu from './components/HeaderMobileMenu';

const Header = React.forwardRef(({companyDetails, categories, featuredVouchers = [], onCategorySelect, onAllGiftsClick, onFeaturedClick, onAboutClick}, ref) => {
  const {t} = useTranslation();
  const {getTranslatedName} = useVoucherTranslation(companyDetails);
  const theme = useTheme();
  const isMdUp = useResponsive('up', 'lg'); // Passe en mode mobile jusqu'à lg (1200px) au lieu de md (900px)
  const {allLangs, currentLang, onChangeLang} = useLocales();

  // Réinitialiser la langue si le multilingue est désactivé
  useLanguageReset(companyDetails);

  // Langues proposées selon la configuration multilingue de l'entreprise
  // (liste vide si le multilingue est désactivé — le sélecteur se masque seul)
  const multiLanguageConfig = companyDetails?.multiLanguage;
  const isMultiLanguageEnabled = multiLanguageConfig?.enabled || false;
  const enabledLanguages = multiLanguageConfig?.enabledLanguages || ['fr'];
  const availableLanguageCodes = isMultiLanguageEnabled ?
    allLangs.filter((lang) => enabledLanguages.includes(lang.value)).map((lang) => lang.value) :
    [];

  const location = useLocation();
  const {slug} = useParams();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const logoUrl = companyDetails?.logoPath ? `${companyDetails.logoPath}` : null;
  const homeUrl = paths.company.home.replace(':slug', companyDetails?.slug);
  const primaryColor = companyDetails?.primaryColor || theme.palette.primary.main;

  const hasFeatured = Boolean(featuredVouchers && featuredVouchers.length > 0);

  // Vente de bons cadeaux disponible : quand le module est désactivé ou la vente
  // en ligne coupée, le menu d'achat (coups de cœur, bons, catégories, à propos)
  // n'a plus rien à proposer et reste entièrement masqué
  const isSaleAvailable = isGiftVoucherOnlineSaleAvailable(companyDetails);

  // La navigation n'est proposée que sur la page d'accueil ("/:slug"), et
  // seulement quand la page a effectivement fourni ses callbacks (via le
  // contexte du layout) : évite de monter nav et drawer avec des props absentes
  const isHomePage = location.pathname === `/${slug}`;
  const isNavAvailable = isHomePage && isSaleAvailable && Boolean(onAllGiftsClick);

  // Header « vivant » : posé en haut de page, ombre + fond translucide flouté
  // dès que la page défile sous lui
  const isScrolled = useScrollTrigger({disableHysteresis: true, threshold: 8});

  return (
    <AppBar
      ref={ref}
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: (muiTheme) => (isScrolled ?
          alpha(muiTheme.palette.background.paper, 0.85) :
          muiTheme.palette.background.paper),
        backdropFilter: isScrolled ? 'blur(8px)' : 'none',
        // Pas de trait dur sous le header : une ombre douce au repos, plus
        // marquée dès que la page défile
        boxShadow: isScrolled ?
          '0 2px 16px rgba(0, 0, 0, 0.08)' :
          '0 1px 3px rgba(0, 0, 0, 0.04)',
        transition: 'box-shadow 0.25s ease, background-color 0.25s ease',
      }}
    >
      {/* Liseré de marque : rappel discret de la couleur de l'établissement */}
      <Box sx={{height: 3, background: createGradientFromColor(primaryColor) || primaryColor}} />

      <HeaderAnnouncementBar
        announcements={companyDetails?.announcements}
        primaryColor={primaryColor}
      />

      {/* Barre principale : logo, navigation centrée, actions à droite */}
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            minHeight: {xs: '70px', md: '80px'},
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr auto', // Mobile: logo flexible + actions
              lg: isHomePage ? '1fr 2fr 1fr' : '1fr auto', // Desktop: logo + navigation centrée + actions
            },
            alignItems: 'center',
            gap: 2,
          }}
        >
          {/* Logo */}
          <Box sx={{flexShrink: 0, justifySelf: 'start'}}>
            <Link to={homeUrl} style={{display: 'flex', alignItems: 'center', height: '100%'}}>
              {logoUrl ? (
                <CardMedia
                  component="img"
                  image={logoUrl}
                  alt={t('header.logoAlt')}
                  sx={{
                    height: {xs: '50px', md: '60px'},
                    maxHeight: {xs: 'calc(70px - 20px)', md: 'calc(80px - 20px)'}, // Hauteur du header - padding
                    maxWidth: {xs: '200px', md: '300px'},
                    objectFit: 'contain',
                    transition: 'all 0.2s ease',
                  }}
                />
              ) : (
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {companyDetails?.name}
                </Typography>
              )}
            </Link>
          </Box>

          {isMdUp && isNavAvailable && (
            <HeaderDesktopNav
              categories={categories}
              hasFeatured={hasFeatured}
              primaryColor={primaryColor}
              getTranslatedName={getTranslatedName}
              onFeaturedClick={onFeaturedClick}
              onAllGiftsClick={onAllGiftsClick}
              onCategorySelect={onCategorySelect}
              onAboutClick={onAboutClick}
            />
          )}

          {/* Actions à droite : langue + burger mobile */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{justifySelf: 'end'}}>
            <FlowLanguageSelector
              currentLanguage={currentLang.value}
              availableLanguages={availableLanguageCodes}
              onLanguageChange={onChangeLang}
              labelDisplay={isMdUp ? 'code' : 'flag'}
              ariaLabel={t('header.language', 'Langue')}
            />

            {!isMdUp && isNavAvailable && (
              <IconButton
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                size="large"
                sx={{
                  'ml': 1,
                  'color': 'text.primary',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <Iconify icon="mdi:menu" width={28} />
              </IconButton>
            )}
          </Stack>
        </Toolbar>
      </Container>

      {/* Menu mobile (drawer) — monté uniquement quand la navigation existe */}
      {isNavAvailable && (
        <Drawer
          anchor="left"
          open={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              boxShadow: (drawerTheme) => drawerTheme.shadows[8],
            },
          }}
        >
          <HeaderMobileMenu
            companyName={companyDetails?.name}
            categories={categories}
            hasFeatured={hasFeatured}
            primaryColor={primaryColor}
            getTranslatedName={getTranslatedName}
            availableLanguages={availableLanguageCodes}
            currentLanguage={currentLang.value}
            onChangeLang={onChangeLang}
            onFeaturedClick={onFeaturedClick}
            onAllGiftsClick={onAllGiftsClick}
            onCategorySelect={onCategorySelect}
            onAboutClick={onAboutClick}
            onClose={() => setIsMobileMenuOpen(false)}
          />
        </Drawer>
      )}
    </AppBar>
  );
});

Header.displayName = 'Header';

Header.propTypes = {
  companyDetails: PropTypes.object,
  categories: PropTypes.array,
  featuredVouchers: PropTypes.array,
  // Callbacks de navigation : requis uniquement sur la page d'accueil,
  // les autres pages rendent le header sans menu
  onCategorySelect: PropTypes.func,
  onAllGiftsClick: PropTypes.func,
  onFeaturedClick: PropTypes.func,
  onAboutClick: PropTypes.func,
};

export default Header;
